import { CURVE } from "./curves";
import {
  type Arg,
  type Fn,
  FormulaError,
  type FunctionDef,
  type FunctionRegistry,
  toBoolean,
  toNumber,
} from "./interpreter";
import { PERIOD } from "./period";

function nums(args: Arg[]): number[] {
  const out: number[] = [];
  for (const a of args) for (const v of a.values()) out.push(toNumber(v));
  return out;
}

const sum: Fn = (args) => nums(args).reduce((a, b) => a + b, 0);

const average: Fn = (args) => {
  const n = nums(args);
  if (!n.length) throw new FormulaError("#DIV/0!");
  return n.reduce((a, b) => a + b, 0) / n.length;
};

const min: Fn = (args) => {
  const n = nums(args);
  return n.length ? Math.min(...n) : 0;
};

const max: Fn = (args) => {
  const n = nums(args);
  return n.length ? Math.max(...n) : 0;
};

const count: Fn = (args) => nums(args).length;

const abs: Fn = (args) => Math.abs(toNumber(args[0]!.eval()));

const round: Fn = (args) => {
  const v = toNumber(args[0]!.eval());
  const digits = args[1] ? toNumber(args[1].eval()) : 0;
  const f = 10 ** digits;
  return Math.round(v * f) / f;
};

const iff: Fn = (args) => (toBoolean(args[0]!.eval()) ? args[1]!.eval() : args[2]!.eval());

const NUMBERS = ["number1", "[number2]", "…"] as const;

const sumDef: FunctionDef = { fn: sum, params: NUMBERS, description: "Adds its arguments" };
const averageDef: FunctionDef = { fn: average, params: NUMBERS, description: "Returns the average of its arguments" };
const minDef: FunctionDef = { fn: min, params: NUMBERS, description: "Returns the smallest value" };
const maxDef: FunctionDef = { fn: max, params: NUMBERS, description: "Returns the largest value" };
const countDef: FunctionDef = { fn: count, params: NUMBERS, description: "Counts the numeric values" };
const absDef: FunctionDef = { fn: abs, params: ["number"], description: "Returns the absolute value of a number" };
const roundDef: FunctionDef = {
  fn: round,
  params: ["number", "digits"],
  description: "Rounds a number to a given number of digits",
};
const ifDef: FunctionDef = {
  fn: iff,
  params: ["condition", "value_if_true", "value_if_false"],
  description: "Returns one value if the condition is true and another if it is false",
};

export const BUILTINS: FunctionRegistry = {
  SUM: sumDef,
  AVG: averageDef,
  AVERAGE: averageDef,
  MIN: minDef,
  MINIMUM: minDef,
  MAX: maxDef,
  MAXIMUM: maxDef,
  COUNT: countDef,
  ABS: absDef,
  ROUND: roundDef,
  IF: ifDef,
};

export const FUNCTIONS: FunctionRegistry = { ...BUILTINS, CURVE, PERIOD };
